/**
 * COMMENT API — wrapper REST per i commenti polimorfici.
 *
 * Contratto backend (CommentsResourceImpl):
 * - GET    /comments/{groupId}?className=<fqn>&classPK=<id>  -> Comment[]
 * - POST   /comments/{groupId}  (body: CommentInput)         -> Comment
 * - PATCH  /comment/{commentId}  (body: { body })            -> Comment
 * - DELETE /comment/{commentId}                              -> 200
 *
 * NB: className contiene il FQN Java dell'entità a cui il commento è associato
 * (es. "it.dvel.crm.core.model.Note"); la conversione short->FQN avviene nel
 * consumatore via entityRegistry.
 */

import type { Page } from '@crm/shared';
import { httpClient } from '../../api/httpClient';
import type { Comment, CommentCreatePayload, CommentSource } from './commentTypes';

const COLLECTION = '/comments';
const SINGLE = '/comment';

export const commentApi = {
  /**
   * GET: i commenti dell'entità (className FQN, classPK) nel sito. L'endpoint
   * restituisce un Page Liferay (collection wrappata): si estraggono gli items.
   */
  getComments: (groupId: number, className: string, classPK: number): Promise<Comment[]> =>
    httpClient
      .get<Page<Comment>>(`${COLLECTION}/${groupId}`, { params: { className, classPK } })
      .then((r) => r.data.items),

  /** POST: crea un commento associato all'entità (className FQN, classPK). */
  addComment: (groupId: number, payload: CommentCreatePayload): Promise<Comment> =>
    httpClient.post<Comment>(`${COLLECTION}/${groupId}`, payload).then((r) => r.data),

  /**
   * PATCH: aggiorna il solo `body` di un commento. className/classPK sono
   * ignorati dal server, quindi si invia il body parziale. `source` (path +
   * etichetta del FE) alimenta la mail di avviso per le menzioni NUOVE.
   */
  updateComment: (commentId: number, body: string, source?: CommentSource): Promise<Comment> =>
    httpClient.patch<Comment>(`${SINGLE}/${commentId}`, { body, ...source }).then((r) => r.data),

  /** DELETE: cancella un commento per id. */
  deleteComment: (commentId: number): Promise<void> =>
    httpClient.delete(`${SINGLE}/${commentId}`).then(() => undefined),
};
